// =============================================================================
// timetable-search.js – Search (city / line / stop / via) for the Orari tab
// =============================================================================
// Reuses the LIVE tab's stop-search visual pattern (`.stop-search-wrapper` /
// `.stop-search-results` / `.search-result-item` / `.search-chip`) and the
// shared `trasporti_search_history` localStorage history so recent searches
// show up in both tabs.
//
// Three result groups:
//   1. Città   — comuni serviti (nomi completi o abbreviati, es. "san
//                vittore olona", "sv olona") con le linee che li servono
//   2. Linee   — match on lineConfig id/label (e.g. "z649", "649", "bus…")
//   3. Fermate — match on STOP_NAMES (includes comune + via) and code prefix
//
// Picking a stop:
//   - if the active line in the active direction serves it → set as Da (FROM)
//   - otherwise, switch to the first served line (among the city lines) and
//     set it as Da. Lines are lazy-loaded via loadLines if not yet cached.
// =============================================================================

import { escapeHtml } from "./utils.js";
import { CITY_FULL_NAMES, LINE_CONFIG, STOP_NAMES, getStopName } from "./line-config.js";
import { getScheduleKey, resolveDirection } from "./utils.js";

const MAX_LINE_RESULTS = 8;
const MAX_STOP_RESULTS = 12;
const MAX_CITY_RESULTS = 3;
const MAX_CITY_STOP_RESULTS = 8;

// Abbreviazioni usate in STOP_NAMES e nelle label delle linee → forma
// estesa. Vengono applicate DOPO la normalizzazione (punteggiatura →
// spazi), quindi "S.V. Olona" arriva qui come "s v" e "Busto G." come
// "busto g"; così funzionano anche le query abbreviate ("sv olona").
const ABBREV_EXPANSIONS = [
  [/\bs\s+v\b/g, "san vittore"],
  [/\bsv\b/g, "san vittore"],
  [/\bs\s+giorgio\b/g, "san giorgio"],
  [/\bs\s+s\s+ticino\b/g, "santo stefano ticino"],
  [/\bss\s+ticino\b/g, "santo stefano ticino"],
  [/\bbusto\s+g\b/g, "busto garolfo"],
  [/\bbusto\s+a\b/g, "busto arsizio"],
  [/\bcastano\s+p\b/g, "castano primo"],
  [/\bcerro\s+m\b/g, "cerro maggiore"],
  [/\bvilla\s+c\b/g, "villa cortese"],
  [/\bpregnana\s+m\b/g, "pregnana milanese"],
  [/\bbernatete\s+t\b/g, "bernatete ticino"],
  [/\brobecchetto\s+c\b/g, "robecchetto con"]
];

const stripAccents = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/** Normalizza un testo per il matching: lowercase, niente accenti,
 *  punteggiatura → spazi, abbreviazioni note espanse. */
export function normalizeSearchText(text) {
  let s = stripAccents(String(text || "")).toLowerCase().replace(/[^a-z0-9]+/g, " ");
  for (const [pattern, full] of ABBREV_EXPANSIONS) s = s.replace(pattern, full);
  return s.replace(/\s+/g, " ").trim();
}

const tokenize = (text) => normalizeSearchText(text).split(" ").filter(Boolean);

/** True quando ogni token della query è uguale a — o prefisso di (min.
 *  3 caratteri) — un token del testo. Permette "san vittore" → "san
 *  vittore olona" e "castano" → "castano primo". */
export function tokensMatch(queryTokens, text) {
  const target = tokenize(text);
  if (!queryTokens.length || !target.length) return false;
  return queryTokens.every((qt) =>
    target.some((tt) => tt === qt || (qt.length >= 3 && tt.startsWith(qt)))
  );
}

let cityIndexCache = null;

/** Indice comuni → codici fermata, derivato dai nomi in STOP_NAMES
 *  ("Comune, via"), con i nomi abbreviati espansi via CITY_FULL_NAMES. */
function getCityIndex() {
  if (cityIndexCache) return cityIndexCache;
  const index = new Map();
  for (const [code, name] of Object.entries(STOP_NAMES)) {
    const raw = String(name).split(",")[0].trim();
    if (!raw) continue;
    const city = CITY_FULL_NAMES[raw] || raw;
    let entry = index.get(city);
    if (!entry) {
      entry = { name: city, codes: [] };
      index.set(city, entry);
    }
    if (!entry.codes.includes(code)) entry.codes.push(code);
  }
  cityIndexCache = index;
  return index;
}

const stopCityCache = new Map();

/** Comune (nome completo) di un codice fermata, "" se ignoto. */
function getStopCity(code) {
  if (stopCityCache.has(code)) return stopCityCache.get(code);
  const raw = String(STOP_NAMES[code] || "").split(",")[0].trim();
  const city = raw ? (CITY_FULL_NAMES[raw] || raw) : "";
  stopCityCache.set(code, city);
  return city;
}

/** Linee (id) che servono un comune, dedotte da label, destinazione di
 *  ritorno e referenceStops di LINE_CONFIG (senza caricare i dati GTFS). */
function getLinesForCity(cityEntry, lineConfig) {
  const cityTokens = tokenize(cityEntry.name);
  const lines = [];
  for (const [id, cfg] of Object.entries(lineConfig)) {
    const label = [cfg?.label, cfg?.returnDestination].filter(Boolean).join(" ");
    const serves = tokensMatch(cityTokens, label) ||
      (cfg?.referenceStops || []).some((c) => getStopCity(c) === cityEntry.name);
    if (serves) lines.push(id);
  }
  return lines;
}

/** Compute the search input + results dropdown HTML to drop into .page-head.
 *  Returns "" if hidden by the caller. */
export function renderTimetableSearchHTML() {
  return `
    <div class="stop-search-wrapper tt-search-wrapper" data-timetable-search>
      <svg class="search-input-icon" viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
      <input type="text" class="stop-search-input" data-timetable-search-input
        placeholder="Cerca linea, fermata o città…" autocomplete="off">
      <div class="stop-search-results" data-timetable-search-results></div>
    </div>
  `;
}

/** Build the results dropdown HTML for a query. */
export function buildTimetableSearchResults(query, ctx) {
  if (!query || query.length < 1) return "";
  const raw = query.toLowerCase().trim();
  if (!raw) return "";
  const { lineConfig = LINE_CONFIG, activeLine = null } = ctx || {};
  const qTokens = tokenize(query);
  if (!qTokens.length) return "";

  let html = "";

  // Group 0: Città — nomi completi o abbreviati ("san vittore olona",
  // "sv olona", "castano"…), con le linee che li servono. Solo informativa:
  // si selezionano le linee (gruppo sotto) o le fermate.
  const cityMatches = [...getCityIndex().values()]
    .filter((c) => tokensMatch(qTokens, c.name))
    .slice(0, MAX_CITY_RESULTS);
  if (cityMatches.length) {
    html += `<div class="search-group-label">Città</div>`;
    for (const city of cityMatches) {
      const lines = getLinesForCity(city, lineConfig);
      const title = lines.length
        ? `${city.name} — servita da: ${lines.join(", ")}`
        : city.name;
      html += `<div class="search-result-item" title="${escapeHtml(title)}">
        <strong>${escapeHtml(city.name)}</strong>${lines.length ? ` · Linee: ${escapeHtml(lines.join(", "))}` : ""}
      </div>`;
    }
  }

  // Group 1: Linee (id + label, con abbreviazioni espanse).
  const lineMatches = Object.entries(lineConfig)
    .filter(([id, cfg]) =>
      id.toLowerCase().includes(raw) || tokensMatch(qTokens, cfg?.label || "")
    )
    .slice(0, MAX_LINE_RESULTS);
  if (lineMatches.length) {
    html += `${cityMatches.length ? `<div style="height:6px;"></div>` : ""}<div class="search-group-label">Linee</div>`;
    for (const [id, cfg] of lineMatches) {
      const isCurrent = id === activeLine;
      html += `<button type="button" class="search-result-item${isCurrent ? " selected" : ""}" data-tt-line-pick="${escapeHtml(id)}">
        <strong>${escapeHtml(id)}</strong>${cfg?.label ? ` · ${escapeHtml(cfg.label)}` : ""}${isCurrent ? " ✓" : ""}
      </button>`;
    }
  }

  // Group 2: Fermate — prima quelle dei comuni trovati (max 8), poi le
  // altre corrispondenze (STOP_NAMES + codice).
  const cityStopCodes = [];
  for (const city of cityMatches) {
    for (const code of city.codes) {
      if (!cityStopCodes.includes(code)) cityStopCodes.push(code);
      if (cityStopCodes.length >= MAX_CITY_STOP_RESULTS) break;
    }
    if (cityStopCodes.length >= MAX_CITY_STOP_RESULTS) break;
  }
  const otherStopCodes = Object.keys(STOP_NAMES).filter((c) =>
    !cityStopCodes.includes(c) &&
    (tokensMatch(qTokens, STOP_NAMES[c]) || c.toLowerCase().includes(raw))
  );
  const stopCodes = cityStopCodes.concat(otherStopCodes).slice(0, MAX_STOP_RESULTS);
  if (stopCodes.length) {
    html += `${lineMatches.length || cityMatches.length ? `<div style="height:6px;"></div>` : ""}<div class="search-group-label">Fermate</div>`;
    for (const code of stopCodes) {
      html += `<button type="button" class="search-result-item" data-tt-stop-pick="${escapeHtml(code)}" title="${escapeHtml(STOP_NAMES[code])} [${escapeHtml(code)}]">${escapeHtml(getStopName(code))}</button>`;
    }
  }

  return html || `<div class="search-no-results">Nessun risultato per "${escapeHtml(query)}"</div>`;
}

/** Find the first line (among `candidateLineIds`) whose current-direction
 *  trips serve `stopCode`. Returns the lineId or null. */
export function findLineServingStop(stopCode, candidateLineIds, lineData, dayType, baseDirection, settings) {
  const direction = resolveDirection(baseDirection || "outbound", settings);
  for (const lineId of candidateLineIds) {
    const trips = lineData?.[lineId]?.[getScheduleKey(lineId, dayType, direction)] || [];
    if (trips.some((t) => t.stops && t.stops[stopCode] != null)) return lineId;
    // Fallback: same direction, other day-types (handles today=sunday but line
    // only has weekday_outbound — we still want to set it as Da).
    for (const dt of ["weekday", "saturday", "sunday"]) {
      if (dt === dayType) continue;
      const k = getScheduleKey(lineId, dt, direction);
      const ts = lineData?.[lineId]?.[k] || [];
      if (ts.some((t) => t.stops && t.stops[stopCode] != null)) return lineId;
    }
  }
  return null;
}

/** True when the line's current-direction route (any day-type) contains the
 *  stop. Used to decide between "set as Da directly" vs "switch line first". */
export function lineServesStop(lineId, stopCode, lineData, direction) {
  for (const dt of ["weekday", "saturday", "sunday"]) {
    const k = getScheduleKey(lineId, dt, direction);
    const trips = lineData?.[lineId]?.[k] || [];
    if (trips.some((t) => t.stops && t.stops[stopCode] != null)) return true;
  }
  return false;
}

/** Push a stop code into the shared search history (capped at 3, most-recent
 *  first). Same storage as the LIVE tab so chips appear in both. */
export function pushSearchHistory(stopCode) {
  try {
    let history = JSON.parse(localStorage.getItem("trasporti_search_history") || "[]");
    history = [stopCode, ...history.filter((c) => c !== stopCode)].slice(0, 3);
    localStorage.setItem("trasporti_search_history", JSON.stringify(history));
  } catch { /* ignore */ }
}
